import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type AcademicYear = components["schemas"]["AcademicYearDetails"];
export type ClassListItem = components["schemas"]["ClassListItem"];
export type ClassDetails = components["schemas"]["ClassDetails"];
export type ClassSubject = components["schemas"]["ClassSubjectDetails"];
export type TeacherAssignment = components["schemas"]["TeacherAssignmentDetails"];
export type Course = components["schemas"]["CourseDetails"];
export type Teacher = components["schemas"]["TeacherDetails"];

export const classKeys = {
  all: ["academic", "classes"] as const,
  list: (yearId: string | undefined) => ["academic", "classes", "list", yearId] as const,
  detail: (id: string) => ["academic", "classes", "detail", id] as const,
  teachers: (id: string) => ["academic", "classes", "teachers", id] as const,
};

/** Display format for API dates (yyyy-MM-dd). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${d}.${m}.${y}`;
}

export function useAcademicYears() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["academic", "years"],
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/years");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

export function useCourses() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["academic", "courses"],
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/courses");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

/** Active teachers, for choosing who teaches a subject (inactive teachers cannot receive new assignments). */
export function useActiveTeachers(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["teachers", "active-for-assignment"],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/teachers", { params: { query: { IsActive: true, Page: 1, PageSize: 200 } } });
      if (error || !data) throw new Error(t("app.error"));
      return data.items;
    },
  });
}

/** The year shown by default: the active one, otherwise the most recent. */
export function defaultYear(years: AcademicYear[] | undefined): AcademicYear | undefined {
  if (!years || years.length === 0) return undefined;
  return years.find((y) => y.status === "Active") ?? [...years].sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
}
